import { useState, type JSX } from 'react';
import { Button } from '@/components';
import { SurfaceCard } from '@/components/blocks/data-display';
import { EmptyState } from '@/components/blocks/feedback';
import { Heading3 } from '@/components/blocks/typography';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import type { ArrayItemFieldConfig } from '../config/schemas/types';
import { EmbeddedArrayItemModal } from './EmbeddedArrayItemModal';

/**
 * Props for EmbeddedArrayTable component.
 */
export interface EmbeddedArrayTableProps<T extends { id: string }> {
  /** Section title displayed above the table */
  title: string;
  /** Array items to display */
  items: T[];
  /** Field configuration for table columns and modal form */
  itemFields: readonly ArrayItemFieldConfig[];
  /** Label for add button */
  addLabel: string;
  /** Message shown when array is empty */
  emptyMessage?: string;
  /** Modal title for add/edit */
  modalTitle?: string;
  /** Default values for new items */
  defaultValues?: Partial<T>;
  /** Callback to save the updated array */
  onUpdate: (items: T[]) => Promise<void>;
  /** Custom formatters for display values */
  formatters?: Record<string, (value: unknown) => string>;
  /** Catalog ID to label maps for resolving multi-select values */
  catalogLabelMaps?: Record<string, Map<string, string>>;
  /** Whether an update is in progress */
  isUpdating?: boolean;
}

/**
 * Schema-driven table for embedded arrays with per-row modal editing.
 *
 * Features:
 * - h3 section header with add button
 * - SurfaceCard containing table
 * - Row actions (edit/delete)
 * - Empty state when no items
 * - Per-row modal for add/edit
 *
 * @example
 * ```tsx
 * <EmbeddedArrayTable
 *   title="Soglie di intervento"
 *   items={thresholds}
 *   itemFields={thresholdFields}
 *   addLabel="Aggiungi soglia"
 *   modalTitle="Soglia di intervento"
 *   onUpdate={handleUpdate}
 * />
 * ```
 */
export function EmbeddedArrayTable<T extends { id: string }>({
  title,
  items,
  itemFields,
  addLabel,
  emptyMessage = 'Nessun elemento configurato.',
  modalTitle,
  defaultValues,
  onUpdate,
  formatters,
  catalogLabelMaps,
  isUpdating = false,
}: EmbeddedArrayTableProps<T>): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);

  const handleAdd = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item: T) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleDelete = async (itemId: string) => {
    const updatedItems = items.filter((item) => item.id !== itemId);
    await onUpdate(updatedItems);
  };

  const handleSave = async (itemData: Record<string, unknown>) => {
    if (editingItem) {
      // Update existing item
      const updatedItems = items.map((item) =>
        item.id === editingItem.id ? { ...item, ...itemData } : item
      );
      await onUpdate(updatedItems);
    } else {
      // Add new item
      const newItem = { ...defaultValues, ...itemData } as T;
      await onUpdate([...items, newItem]);
    }
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleModalDelete = async () => {
    if (editingItem) {
      await handleDelete(editingItem.id);
      setModalOpen(false);
      setEditingItem(null);
    }
  };

  const formatValue = (field: ArrayItemFieldConfig, value: unknown): string => {
    // Check custom formatter first
    if (formatters?.[field.key]) {
      return formatters[field.key](value);
    }

    // Handle null/undefined
    if (value === null || value === undefined) return '—';

    // Handle select fields - find label from options
    if (field.type === 'select' && field.options) {
      const option = field.options.find((opt) => opt.value === value);
      return option?.label ?? String(value);
    }

    // Handle multi-select fields - resolve IDs to labels if catalog is available
    if (field.type === 'multi-select' && Array.isArray(value)) {
      if (value.length === 0) return '—';
      // If catalogId is specified and we have a label map, resolve IDs to labels
      if (field.catalogId && catalogLabelMaps?.[field.catalogId]) {
        const labelMap = catalogLabelMaps[field.catalogId];
        return value.map((id) => labelMap.get(id) ?? id).join(', ');
      }
      return value.join(', ');
    }

    return String(value);
  };

  const getColumnWidth = (width?: 'sm' | 'md' | 'lg' | 'flex'): string => {
    switch (width) {
      case 'sm':
        return 'w-[100px]';
      case 'md':
        return 'w-[140px]';
      case 'lg':
        return 'w-[200px]';
      case 'flex':
        return ''; // Will fill remaining space in table-auto layout
      default:
        return 'w-[120px]';
    }
  };

  return (
    <div className="mt-6 space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <Heading3 className="text-brand-blue-800">{title}</Heading3>
        <Button variant="secondary" size="sm" onClick={handleAdd}>
          {addLabel}
        </Button>
      </div>

      {/* Table or Empty State */}
      <SurfaceCard contentClassName="overflow-x-auto">
        {items.length === 0 ? (
          <EmptyState icon="playlist_add" title={emptyMessage} variant="inline" />
        ) : (
          <table className="w-full min-w-[24rem] border-collapse">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-text-secondary">
                {itemFields.map((field) => (
                  <th
                    key={field.key}
                    className={`border-b border-[var(--border-subtle)] pb-3 pr-6 font-semibold ${getColumnWidth(field.width)}`}
                  >
                    {field.label}
                  </th>
                ))}
                <th className="border-b border-[var(--border-subtle)] pb-3 w-[50px]" />
              </tr>
            </thead>
            <tbody className="text-sm text-text-primary">
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-[var(--border-subtle)] last:border-b-0 hover:bg-surface-secondary"
                >
                  {itemFields.map((field) => (
                    <td
                      key={field.key}
                      className={`py-3 pr-6 align-top ${getColumnWidth(field.width)}`}
                    >
                      {formatValue(field, (item as Record<string, unknown>)[field.key])}
                    </td>
                  ))}
                  <td className="py-3 align-top text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <Icon name="more_vert" className="icon-base" />
                          <span className="sr-only">Azioni</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(item)}>
                          <Icon name="edit" className="icon-base mr-2" />
                          Modifica
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDelete(item.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Icon name="delete" className="icon-base mr-2" />
                          Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SurfaceCard>

      {/* Edit/Add Modal */}
      <EmbeddedArrayItemModal
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) setEditingItem(null);
        }}
        title={modalTitle ?? title}
        item={editingItem as Record<string, unknown> | null}
        itemFields={itemFields}
        defaultValues={defaultValues as Record<string, unknown>}
        onSave={handleSave}
        onDelete={editingItem ? handleModalDelete : undefined}
        isSubmitting={isUpdating}
      />
    </div>
  );
}
